import { useMutation } from '@tanstack/react-query';
import { useApiClient } from '~/api-client';
import { useToast } from '@cad-arc';
import {
    AttachmentsResource,
    LinkTypesEnum,
    BeginFileUploadRequest,
    CompleteFileUploadRequest,
} from '@mark43/cad-api';
import { useAttachmentsUploadStore } from '../attachments-upload-store-provider';
import { logAttachmentsError } from '../helpers';
import { AttachmentsPanelType } from '../types';
import { uploadToS3 } from '../helpers/upload-to-s3';
import { v4 as uuidV4 } from 'uuid';
import { AttachmentUploadDeniedError } from '../errors';

type UseAttachmentsBaseParameter = {
    entityId: number;
    entityType: AttachmentsPanelType;
    entityNumber?: string;
    onUploadError?: (params: { error: Error; file: File }) => void;
};

export function useUploadAttachment({
    entityId,
    entityType,
    entityNumber,
    onUploadError,
}: UseAttachmentsBaseParameter) {
    const apiClient = useApiClient();
    const uploadStore = useAttachmentsUploadStore();
    const toast = useToast();

    const processingStateTimeoutCallback = (fileName: string) => {
        toast({
            status: 'default',
            description: `${fileName} is taking longer to process than expected.`,
        });
    };

    return useMutation({
        mutationFn: async (file: File) => {
            let fileServerFileId: string | undefined;

            try {
                // Step 1: Begin upload to get presigned URL and fileServerFileId
                const linkTypeId =
                    entityType === 'BOLO'
                        ? LinkTypesEnum.BOLO_ATTACHMENT
                        : LinkTypesEnum.CAD_AGENCY_EVENT_ATTACHMENT;

                const beginRequest: BeginFileUploadRequest = {
                    fileNames: [file.name],
                    fileNameToFileSize: { [file.name]: file.size },
                    entityType,
                    entityId,
                };

                const beginResponse = await apiClient.request<AttachmentsResource.BeginFileUpload>({
                    method: 'POST',
                    url: 'attachments/upload/begin',
                    data: beginRequest,
                });

                if (
                    !beginResponse.data?.data?.files ||
                    beginResponse.data.data.files.length === 0
                ) {
                    throw new Error('No upload configuration returned from server');
                }

                const fileUpload = beginResponse.data.data.files[0];

                if (fileUpload.isDenied) {
                    throw new AttachmentUploadDeniedError();
                }

                fileServerFileId = fileUpload.fileServerFileId!;
                // Decode HTML entities in the URL (backend may return &amp; instead of &)
                const presignedUrl = fileUpload.url?.replace(/&amp;/g, '&');

                if (!fileServerFileId) {
                    throw new Error('File server file ID not returned from server');
                }
                if (!presignedUrl) {
                    throw new Error('No presigned URL returned from server');
                }

                // Extract S3 configuration from backend response
                // Backend provides s3Bucket, s3Key, and temporaryS3Config for large files (>= 5MB)
                const bucket = fileUpload.s3Bucket;
                const key = fileUpload.s3Key;
                const s3Config = fileUpload.temporaryS3Config;

                // Extract the actual filename from the S3 key path
                // Backend may have sanitized the filename (e.g., replaced parentheses with underscores)
                // during the upload/begin step, so the filename in the S3 key is the "source of truth"
                // We must use this exact filename in the complete request to match what's stored in S3
                const filenameFromS3Key = key?.split('/').pop();
                if (!filenameFromS3Key) {
                    throw new Error('No filename found in S3 key');
                }

                // Add upload to store using fileServerFileId as identifier
                uploadStore.addUpload(fileServerFileId, {
                    entityId,
                    entityType,
                    entityNumber,
                    fileName: file.name,
                    fileSize: file.size,
                });

                // Step 2: Upload to S3
                await uploadToS3({
                    presignedUrl,
                    bucket,
                    key,
                    s3Config,
                    file,
                    expectedContentType: fileUpload.contentType,
                    onProgress: (uploadedBytes) => {
                        if (fileServerFileId) {
                            uploadStore.updateUploadProgress(
                                {
                                    id: fileServerFileId,
                                    uploadedBytes,
                                },
                                uploadedBytes >= file.size
                                    ? () => processingStateTimeoutCallback(file.name)
                                    : undefined
                            );
                        }
                    },
                });

                // Step 3: Complete upload
                // IMPORTANT: Use the filename from the S3 key, not the original file.name
                // The backend may have modified the filename during upload/begin (e.g., sanitizing special chars)
                // We must use the exact filename that's in S3 for the backend validation to succeed
                const completeRequest: CompleteFileUploadRequest = {
                    fileServerFileId,
                    entityType,
                    entityId,
                    linkType: linkTypeId,
                    fileName: filenameFromS3Key,
                };

                const completeResponse =
                    await apiClient.request<AttachmentsResource.CompleteFileUpload>({
                        method: 'POST',
                        url: 'attachments/upload/complete',
                        data: completeRequest,
                    });

                return { data: completeResponse.data, fileServerFileId };
            } catch (error) {
                logAttachmentsError(error as Error);
                onUploadError?.({ error: error as Error, file });

                if (fileServerFileId) {
                    // Mid-upload failure - upload already exists in store
                    uploadStore.markUploadFailed(
                        fileServerFileId,
                        error instanceof Error ? error.message : 'Upload failed'
                    );
                } else {
                    // Early failure - create failed upload to show failed thumbnail
                    uploadStore.addFailedUpload(
                        uuidV4(),
                        {
                            entityId,
                            entityType,
                            entityNumber,
                            fileName: file.name,
                            fileSize: file.size,
                        },
                        error instanceof Error ? error.message : 'Upload failed'
                    );
                }
            }
        },
    });
}
